import SimpleRatingBadge from '@risksmart-app/components/src/simple-rating-badge/SimpleRatingBadge';
import type { RatingOption } from 'src/ratings/ratings';
import { isRiskMatrixRatingKey } from 'src/ratings/ratings';

import SelectFilter from '../filters/SelectFilter';
import { nullDataChartLabel } from '../nullData';
import type { CellInfo, Helpers, ReportFieldType } from './types';

const getRating = ({
  fieldData,
  fieldDef,
  helpers,
}: CellInfo): RatingOption | undefined => {
  if (fieldDef.displayType !== 'rating') {
    throw new Error('rating field type used out of context');
  }

  // Scoring settings: L/I matrix lookup for risk rating keys with L/I meta
  if (
    helpers.hasScoringSettings &&
    fieldData.meta?.likelihood != null &&
    fieldData.meta?.impact != null
  ) {
    const result = helpers.getRatingByLikelihoodAndImpact(
      Number(fieldData.meta.likelihood),
      Number(fieldData.meta.impact)
    );

    return result
      ? { label: result.label, value: result.value, color: result.color }
      : undefined;
  }

  // Scoring settings: value-based lookup for likelihood/impact keys
  if (helpers.hasScoringSettings && fieldDef.ratingKey === 'likelihood') {
    return helpers.getLikelihoodByValue(Number(fieldData.value));
  }
  if (helpers.hasScoringSettings && fieldDef.ratingKey === 'impact') {
    return helpers.getImpactByValue(Number(fieldData.value));
  }

  // Taxonomy fallback: existing i18n value-based lookup
  return helpers.getRatingByValue(
    fieldDef.ratingKey,
    fieldData.value as number | string
  );
};

/**
 * Resolves a rating from the field's own value rather than from its
 * likelihood/impact meta.
 *
 * Charts group by the field value, but the returned `meta` (likelihood/impact)
 * belongs to an arbitrary representative row in the group and is not guaranteed
 * to match the group's value. Deriving the label/colour from the L/I matrix
 * (as `getRating` does) therefore produces phantom categories — e.g. a "Severe"
 * bar for a group that holds no Severe-rated rows (RSP-5958). Chart labels and
 * colours must come from the group key (the value) so they always reflect the
 * data they aggregate. Per-row table cells keep using `getRating`, where the
 * meta belongs to that single row.
 */
const getRatingFromValue = ({
  fieldData,
  fieldDef,
  helpers,
}: CellInfo): RatingOption | undefined => {
  if (fieldDef.displayType !== 'rating') {
    throw new Error('rating field type used out of context');
  }

  const { value } = fieldData;
  if (value === null || value === undefined) {
    return undefined;
  }

  // Scoring settings: value-based lookup for likelihood/impact keys
  if (helpers.hasScoringSettings && fieldDef.ratingKey === 'likelihood') {
    return helpers.getLikelihoodByValue(Number(value));
  }
  if (helpers.hasScoringSettings && fieldDef.ratingKey === 'impact') {
    return helpers.getImpactByValue(Number(value));
  }

  // Scoring settings: rating-level lookup for risk rating keys
  if (
    helpers.hasScoringSettings &&
    isRiskMatrixRatingKey(fieldDef.ratingKey) &&
    helpers.riskRatingOptions.length > 0
  ) {
    return helpers.riskRatingOptions.find(
      (o) => String(o.value) === String(value)
    );
  }

  // Taxonomy fallback: existing i18n value-based lookup
  return helpers.getRatingByValue(fieldDef.ratingKey, value as number | string);
};

export const rating: ReportFieldType = {
  getChartColor(cellData) {
    const rating = getRatingFromValue(cellData);

    return rating?.color;
  },
  getChartLabel(cellData) {
    const rating = getRatingFromValue(cellData);

    return rating?.label ?? nullDataChartLabel();
  },
  exportVal: (cellData) => {
    const rating = getRating(cellData);

    if (rating === null || rating === undefined) {
      return '';
    }

    return rating.label;
  },
  cell: function (cellData) {
    const rating = getRating(cellData);

    return <SimpleRatingBadge rating={rating} />;
  },
  propertyConfig(fieldDef, helpers: Helpers) {
    if (fieldDef.displayType !== 'rating') {
      throw new Error('rating field type used out of context');
    }

    // Scoring settings: use scoring settings options for likelihood/impact
    if (
      helpers.hasScoringSettings &&
      (fieldDef.ratingKey === 'likelihood' || fieldDef.ratingKey === 'impact')
    ) {
      const options =
        fieldDef.ratingKey === 'likelihood'
          ? helpers.likelihoodOptions
          : helpers.impactOptions;

      return {
        key: fieldDef.key,
        groupValuesLabel: fieldDef.groupValuesLabel,
        propertyLabel: fieldDef.propertyLabel,
        operators: ['=', '!='].map((operator) => ({
          operator,
          format: (value) => {
            const match = options.find(
              (o) => String(o.value) === String(value)
            );

            return match?.label ?? '';
          },
          form: (props) => {
            return (
              <SelectFilter
                {...props}
                options={options.map((o) => ({
                  value: String(o.value),
                  label: o.label,
                }))}
              />
            );
          },
        })),
      };
    }

    // Scoring settings: use rating level options for risk rating keys
    if (
      helpers.hasScoringSettings &&
      isRiskMatrixRatingKey(fieldDef.ratingKey) &&
      helpers.riskRatingOptions.length > 0
    ) {
      return {
        key: fieldDef.key,
        groupValuesLabel: fieldDef.groupValuesLabel,
        propertyLabel: fieldDef.propertyLabel,
        operators: ['=', '!='].map((operator) => ({
          operator,
          format: (value) => {
            const match = helpers.riskRatingOptions.find(
              (o) => String(o.value) === String(value)
            );

            return match?.label ?? '';
          },
          form: (props) => {
            return (
              <SelectFilter
                {...props}
                options={helpers.riskRatingOptions.map((o) => ({
                  value: String(o.value),
                  label: o.label,
                }))}
              />
            );
          },
        })),
      };
    }

    // Taxonomy fallback
    return {
      key: fieldDef.key,
      groupValuesLabel: fieldDef.groupValuesLabel,
      propertyLabel: fieldDef.propertyLabel,
      operators: ['=', '!='].map((operator) => ({
        operator,
        format: (value) => {
          const rating = helpers.getRatingByValue(fieldDef.ratingKey, value);

          return rating?.label ?? '';
        },
        form: (props) => {
          return (
            <SelectFilter
              {...props}
              options={helpers
                .getRatingOptions(fieldDef.ratingKey)
                .map((o) => ({
                  value: (o.value as unknown as string) ?? undefined,
                  label: o.label,
                }))}
            />
          );
        },
      })),
    };
  },
};
